import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAdminStoreView } from '../context/AdminStoreViewContext';
import AdminStoreViewBanner from '../components/AdminStoreViewBanner';
import api from '../lib/api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { ArrowRight, Check, LockKeyhole, Menu } from 'lucide-react';
import HeroChromatogram from '@/components/assay/HeroChromatogram';
import ShippingStory from '@/components/assay/ShippingStory';
import { BatchtrueLockup } from '@/components/assay/BatchtrueMark';
import useReveal from '@/components/assay/useReveal';

/*
  Wholesale-first positioning. Catalog counts come from the product API.
  Hero traces are deterministic illustrations, never product-testing evidence.
  COA availability must not be inferred from the illustration or SKU popularity.
*/

const SHELL = 'mx-auto w-full max-w-[1280px] px-5 sm:px-8 lg:px-12';

function Eyebrow({ children, className = '' }) {
  return <p className={`label-eyebrow ${className}`}>{children}</p>;
}

export default function LandingPage() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const { isStoreView } = useAdminStoreView();
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [accessIntent, setAccessIntent] = useState('guest');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [isQualified, setIsQualified] = useState(false);
  const [isOver21, setIsOver21] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [catalogTotal, setCatalogTotal] = useState(null);
  const [categoryCount, setCategoryCount] = useState(null);
  const [stuck, setStuck] = useState(false);

  useEffect(() => {
    if (!loading && user) {
      if (user.role === 'admin' && !isStoreView) navigate('/admin');
      else navigate('/catalog');
    }
  }, [user, loading, isStoreView, navigate]);

  useEffect(() => {
    let active = true;
    api.get('/products', { params: { limit: 500 } }).then((response) => {
      if (!active) return;
      setCatalogTotal(Number.isFinite(response.data.total) ? response.data.total : null);
      setCategoryCount((response.data.categories || []).length);
    }).catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const onScroll = () => setStuck(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useReveal([catalogTotal]);

  const openAccess = (intent = 'guest') => {
    setAccessIntent(intent);
    setShowAccessModal(true);
  };

  const handleRequestAccess = () => {
    if (!acceptedTerms || !isQualified || !isOver21) return;
    navigate(`/register?intent=${accessIntent}`);
  };

  const requirementsMet = acceptedTerms && isQualified && isOver21;

  return (
    <div className="min-h-screen overflow-x-hidden bg-background text-foreground">
      {/* ------------------------------- nav ------------------------------- */}
      <header
        className={`sticky top-0 z-40 border-b bg-background/85 backdrop-blur-md transition-colors duration-300 ${
          stuck ? 'border-border' : 'border-transparent'
        }`}
      >
        <div className={`${SHELL} flex items-center justify-between gap-4 py-4`}>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="load-in load-in-1 flex items-center rounded-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            aria-label="Batchtrue Wholesale home"
          >
            <BatchtrueLockup markClassName="h-7 w-7" />
          </button>

          <nav className="load-in load-in-2 hidden items-center gap-7 md:flex" aria-label="Public navigation">
            <a href="#quality" className="text-[0.82rem] text-muted-foreground transition-colors hover:text-foreground">Quality &amp; COAs</a>
            <a href="#ordering" className="text-[0.82rem] text-muted-foreground transition-colors hover:text-foreground">Ordering</a>
            <a href="#supply" className="text-[0.82rem] text-muted-foreground transition-colors hover:text-foreground">Supply</a>
            <a href="#payment" className="text-[0.82rem] text-muted-foreground transition-colors hover:text-foreground">Payment</a>
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="text-[0.82rem] text-muted-foreground transition-colors hover:text-foreground"
            >
              Sign in
            </button>
            <Button
              onClick={() => openAccess('guest')}
              className="group rounded-full px-5 text-[0.78rem] font-semibold tracking-[0.02em]"
            >
              Create free guest account
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Button>
          </nav>

          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon" aria-label="Open menu">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80 border-border bg-background">
              <SheetTitle className="sr-only">Batchtrue navigation</SheetTitle>
              <div className="mt-10 grid gap-2">
                <a href="#quality" onClick={() => setMobileMenuOpen(false)} className="rounded-sm px-3 py-2 text-sm text-muted-foreground hover:bg-accent hover:text-foreground">Quality &amp; COAs</a>
                <a href="#ordering" onClick={() => setMobileMenuOpen(false)} className="rounded-sm px-3 py-2 text-sm text-muted-foreground hover:bg-accent hover:text-foreground">Ordering</a>
                <a href="#supply" onClick={() => setMobileMenuOpen(false)} className="rounded-sm px-3 py-2 text-sm text-muted-foreground hover:bg-accent hover:text-foreground">Supply</a>
                <a href="#payment" onClick={() => setMobileMenuOpen(false)} className="rounded-sm px-3 py-2 text-sm text-muted-foreground hover:bg-accent hover:text-foreground">Payment</a>
                <Button variant="ghost" className="justify-start" onClick={() => navigate('/login')}>Sign in</Button>
                <Button className="rounded-full" onClick={() => { setMobileMenuOpen(false); openAccess('guest'); }}>Create free guest account</Button>
                <Button variant="outline" className="rounded-full" onClick={() => { setMobileMenuOpen(false); openAccess('member'); }}>I have an invite code</Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </header>

      <AdminStoreViewBanner className="top-[72px]" />

      <main id="main-content" tabIndex={-1}>
        {/* ------------------------------ hero ----------------------------- */}
        <section className="py-12 sm:py-16 lg:py-20">
          <div className={`${SHELL} grid gap-10`}>
            <div className="grid min-w-0 gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] lg:items-end lg:gap-16">
              <div className="flex min-w-0 flex-col items-start gap-6">
              <Eyebrow className="load-in load-in-1">Wholesale purchasing. Research use only.</Eyebrow>

              <h1 className="load-in load-in-2 font-display text-[clamp(2.3rem,6vw,4.2rem)]">
                Direct wholesale pricing. Built for resellers.
              </h1>
              </div>
              <div className="flex min-w-0 flex-col items-start gap-5">
              <p className="load-in load-in-3 max-w-[44ch] text-[clamp(1rem,1.4vw,1.1rem)] leading-relaxed text-muted-foreground">
                Research compounds by the kit. COAs available on select products.
              </p>

              <div className="load-in load-in-4 mt-2 flex flex-wrap gap-3">
                <Button
                  size="lg"
                  onClick={() => openAccess('guest')}
                  className="group rounded-full px-6 text-[0.82rem] font-semibold tracking-[0.02em]"
                  data-testid="request-access-button"
                >
                  View wholesale pricing
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => openAccess('member')}
                  className="rounded-full px-6 text-[0.82rem] font-semibold tracking-[0.02em]"
                >
                  Apply for wholesale access
                </Button>
              </div>

              <p className="load-in load-in-5 max-w-[52ch] text-[0.78rem] leading-relaxed text-faint">
                Research use only. Not for human or veterinary use. One kit is ten vials.
              </p>

              <button
                type="button"
                onClick={() => navigate('/login')}
                className="load-in load-in-5 rounded-sm text-[0.8rem] text-muted-foreground underline underline-offset-4 transition-colors hover:text-foreground"
              >
                Already have an account? Sign in
              </button>
              </div>
            </div>

            <div className="load-in load-in-4 min-w-0">
              <HeroChromatogram />
            </div>
          </div>
        </section>

        <div className="border-y border-border bg-sunk">
          <dl className={`${SHELL} grid grid-cols-2 gap-6 py-6 sm:grid-cols-3`}>
            <div>
              <dt className="label-quiet">Catalog products</dt>
              <dd className="mt-2 h-8 font-mono text-2xl">{catalogTotal ?? <span aria-label="Product count unavailable">...</span>}</dd>
            </div>
            <div>
              <dt className="label-quiet">Product categories</dt>
              <dd className="mt-2 h-8 font-mono text-2xl">{categoryCount ?? <span aria-label="Category count unavailable">...</span>}</dd>
            </div>
            <div>
              <dt className="label-quiet">Wholesale access</dt>
              <dd className="mt-2 text-sm">Free to browse. Approval to purchase.</dd>
            </div>
          </dl>
        </div>

        <section id="quality" className={`${SHELL} scroll-mt-24 py-16 sm:py-24`}>
          <div className="reveal grid gap-8 border-b border-border pb-12 lg:grid-cols-2 lg:gap-16">
            <div className="flex flex-col gap-4">
              <Eyebrow>Quality &amp; documentation</Eyebrow>
              <h2 className="font-display text-[clamp(1.8rem,3.6vw,2.6rem)]">
                Know what you are buying.
              </h2>
              <p className="text-[0.98rem] leading-relaxed text-muted-foreground">
                Industry-standard quality control, with current certificates of analysis
                (COAs) available by request for selected products in our most popular range.
              </p>
            </div>
            <div className="flex flex-col items-start gap-4 lg:pt-9">
              <p className="text-[0.98rem] leading-relaxed text-muted-foreground">
                Ask which products have a current COA before you order. Testing is not
                available for every product. Review the report date and the product and
                lot it covers, rather than assuming the whole catalog is tested.
              </p>
              <Button variant="outline" className="rounded-full" onClick={() => openAccess('guest')}>
                Browse products and documentation
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </section>

        {/* ------------------------------ argument -------------------------- */}
        <section id="ordering" className={`${SHELL} scroll-mt-24 py-16 sm:py-24`}>
          <div className="reveal mb-12 flex max-w-[54ch] flex-col gap-3">
            <Eyebrow>How it works</Eyebrow>
            <h2 className="font-display text-[clamp(1.8rem,3.6vw,2.6rem)]">
              Three steps from price sheet to reorder.
            </h2>
          </div>

          <ol className="reveal grid grid-cols-1 gap-y-10 border-t border-border lg:grid-cols-12 lg:gap-x-10">
            {[
              {
                n: '01',
                span: 'lg:col-span-5',
                h: 'Open a free guest account',
                p: 'The full catalog and every per-kit price are visible immediately. You evaluate the entire price sheet before you talk to anyone. No DM, no callback, no waiting room.',
              },
              {
                n: '02',
                span: 'lg:col-span-4 lg:border-l lg:border-border lg:pl-10',
                h: 'Apply for wholesale approval',
                p: 'Approval is reviewed by a person, not an algorithm. This keeps the network wholesale, not retail. If you hold an invite code, there is a dedicated path through the same review.',
              },
              {
                n: '03',
                span: 'lg:col-span-3 lg:border-l lg:border-border lg:pl-10',
                h: 'Order by kit, reorder by SKU',
                p: 'One kit is ten vials. Compare the listed kit price and specification, confirm availability, and review any documentation you need before placing an order.',
              },
            ].map((step) => (
              <li key={step.n} className={`flex min-w-0 flex-col gap-3 pt-8 ${step.span}`}>
                <span className="font-mono text-[1.6rem] leading-none tracking-[-0.02em] text-primary">{step.n}</span>
                <h3 className="font-display text-[clamp(1.15rem,2vw,1.45rem)]">{step.h}</h3>
                <p className="text-[0.9rem] leading-relaxed text-muted-foreground">{step.p}</p>
              </li>
            ))}
          </ol>

          <p className="reveal mt-10 max-w-[72ch] border-t border-border pt-6 text-[0.8rem] leading-relaxed text-faint">
            Orders move from the manufacturer to a U.S. warehouse, then to final delivery. Typical fulfillment is 7 to 14 days, an estimate rather than a guarantee.
          </p>
        </section>

        {/* ------------------------------ supply ---------------------------- */}
        <section id="supply" className={`${SHELL} scroll-mt-24 py-16 sm:py-24`}>
          <div className="reveal mb-12 flex max-w-[56ch] flex-col gap-3">
            <Eyebrow>Fulfillment preview</Eyebrow>
            <h2 className="font-display text-[clamp(1.8rem,3.6vw,2.6rem)]">
              See how an order moves, before you place one.
            </h2>
            <p className="text-[0.98rem] leading-relaxed text-muted-foreground">
              See what moved. Know what comes next. Approved customers can follow
              warehouse handoffs, parcel tracking, and team status updates in their
              portal. The journey below shows the two-leg view used for replenishment
              and final delivery.
            </p>
          </div>

          <ShippingStory />

          <div className="reveal mt-6 grid gap-5 text-sm leading-relaxed text-muted-foreground sm:grid-cols-2">
            <p><span className="mb-1 block font-medium text-foreground">Already in US stock?</span>Your order can begin at the warehouse. Confirm availability with the team before purchasing.</p>
            <p><span className="mb-1 block font-medium text-foreground">Waiting on replenishment?</span>The illustrated inbound trip comes first. Warehouse receipt and delivery to you remain separate milestones.</p>
          </div>

          <p className="reveal mt-10 max-w-[72ch] border-t border-border pt-6 text-[0.8rem] leading-relaxed text-faint">
            Carrier scans appear when carriers report them. Delivery windows are estimates, not guarantees.
            {' '}For items not held in US stock, typical fulfillment runs 7 to 14 days from
            order, an estimate based on current lanes, not a guarantee.
          </p>
        </section>

        {/* ----------------------------- payment ---------------------------- */}
        <section id="payment" className={`${SHELL} scroll-mt-24 py-16 sm:py-24`}>
          <div className="reveal mb-10 flex max-w-[56ch] flex-col gap-3">
            <Eyebrow>Payment setup</Eyebrow>
            <h2 className="font-display text-[clamp(1.8rem,3.6vw,2.6rem)]">Payment providers are in setup and not yet live.</h2>
            <p className="text-[0.98rem] leading-relaxed text-muted-foreground">
              Every payment method on this site is an explicit test placeholder. No method can
              currently accept a live payment. Nothing is charged until providers are fully
              configured and live.
            </p>
          </div>

          <div className="reveal grid gap-4 sm:grid-cols-3">
            {[
              {
                h: 'ACH transfer',
                d: 'Expected default for larger orders, with terms set per account once a provider is live.',
              },
              {
                h: 'Direct bank link',
                d: 'A bank to bank path for repeat buyers who prefer not to re enter details on every order.',
              },
              {
                h: 'Card processing',
                d: 'For smaller orders and first time accounts, subject to the processor selected and its limits.',
              },
            ].map((card) => (
              <div
                key={card.h}
                className="flex min-w-0 flex-col items-start gap-2 rounded-lg border border-dashed border-border p-5"
              >
                <span className="rounded-full bg-[hsl(var(--notice))] px-2.5 py-1 font-mono text-[0.62rem] font-semibold uppercase tracking-[0.1em] text-[hsl(var(--notice-foreground))]">
                  Test placeholder, not live
                </span>
                <h3 className="mt-1 text-[0.98rem] font-semibold">{card.h}</h3>
                <p className="text-[0.85rem] leading-relaxed text-muted-foreground">{card.d}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ---------------------------- closing CTA -------------------------- */}
        <section className="border-t border-border bg-sunk">
          <div className={`${SHELL} reveal flex flex-col items-start gap-6 py-16 sm:py-24`}>
            <div className="flex max-w-[46ch] flex-col gap-4">
              <Eyebrow>Full access</Eyebrow>
              <h2 className="font-display text-[clamp(1.9rem,3.8vw,2.8rem)]">
                See the whole catalog before you commit.
              </h2>
              <p className="leading-relaxed text-muted-foreground">
                Open a free guest account to compare products, specifications, and pricing.
                Build your shortlist, request the available documentation, and apply for
                wholesale approval when you are ready.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button
                size="lg"
                onClick={() => openAccess('guest')}
                className="group rounded-full px-6 text-[0.82rem] font-semibold tracking-[0.02em]"
              >
                Create free guest account
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => navigate('/login')}
                className="rounded-full px-6 text-[0.82rem] font-semibold tracking-[0.02em]"
              >
                Sign in
              </Button>
            </div>
          </div>
        </section>
      </main>

      {/* ------------------------------ footer ----------------------------- */}
      <footer className="border-t border-border py-10">
        <div className={SHELL}>
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="flex items-center rounded-sm text-foreground"
              aria-label="Batchtrue Wholesale home"
            >
              <BatchtrueLockup markClassName="h-7 w-7" />
            </button>
            <span className="label-quiet font-mono">© 2026 Batchtrue Wholesale</span>
          </div>
          <p className="max-w-[78ch] text-[0.74rem] leading-relaxed text-faint">
            Research use only. Not for human or veterinary use. One kit is ten vials. A free guest
            account can view the catalog and pricing. Membership, meaning wholesale approval, is
            required to purchase. Orders move from the manufacturer to a U.S. warehouse, then to final
            delivery. Typical fulfillment is 7 to 14 days, an estimate rather than a guarantee.
            Payment methods shown are test placeholders pending provider selection.
          </p>
        </div>
      </footer>

      {/* --------------------------- access dialog ------------------------- */}
      <Dialog open={showAccessModal} onOpenChange={setShowAccessModal}>
        <DialogContent className="max-h-[92dvh] overflow-y-auto border-border bg-card sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Choose your access path</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              A free guest account can view the catalog and pricing. Membership approval is required before
              purchasing.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-3 sm:grid-cols-2" role="radiogroup" aria-label="Account access type">
            {[
              {
                key: 'guest',
                eyebrow: 'Free guest account',
                title: 'Browse catalog and pricing',
                detail: 'Search, compare, and build a cart. Order submission stays locked.',
              },
              {
                key: 'member',
                eyebrow: 'Purchasing membership',
                title: 'Apply to place orders',
                detail: 'Enter your invite code. Ordering is enabled only after account approval.',
              },
            ].map((option) => (
              <button
                key={option.key}
                type="button"
                role="radio"
                aria-checked={accessIntent === option.key}
                onClick={() => setAccessIntent(option.key)}
                className={`relative flex flex-col gap-1 rounded-lg border p-4 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                  accessIntent === option.key ? 'border-primary bg-accent' : 'border-border hover:border-primary/50'
                }`}
              >
                <span className="label-quiet">{option.eyebrow}</span>
                <strong className="text-[0.95rem] font-semibold">{option.title}</strong>
                <small className="text-[0.8rem] text-muted-foreground">{option.detail}</small>
                {accessIntent === option.key ? (
                  <Check aria-hidden="true" className="absolute right-3 top-3 h-4 w-4 text-primary" />
                ) : null}
              </button>
            ))}
          </div>

          <div className="flex flex-col gap-3">
            <label className="flex items-start gap-3 text-[0.85rem] text-muted-foreground">
              <Checkbox checked={isOver21} onCheckedChange={setIsOver21} className="mt-0.5" />
              <span><strong className="text-foreground">Age verification:</strong> I confirm that I am 21 years of age or older.</span>
            </label>
            <label className="flex items-start gap-3 text-[0.85rem] text-muted-foreground">
              <Checkbox checked={isQualified} onCheckedChange={setIsQualified} className="mt-0.5" />
              <span><strong className="text-foreground">Research purchaser:</strong> I am acquiring products for lawful research or analytical use.</span>
            </label>
            <label className="flex items-start gap-3 text-[0.85rem] text-muted-foreground">
              <Checkbox checked={acceptedTerms} onCheckedChange={setAcceptedTerms} className="mt-0.5" />
              <span><strong className="text-foreground">Research use only:</strong> I understand these products are not intended for human consumption or medical use.</span>
            </label>
          </div>

          <Button
            className="w-full rounded-full text-[0.82rem] font-semibold tracking-[0.02em]"
            disabled={!requirementsMet}
            onClick={handleRequestAccess}
            data-testid="proceed-to-registration"
          >
            {requirementsMet
              ? accessIntent === 'guest' ? 'Create free guest account' : 'Continue with membership invite'
              : 'Confirm all requirements'}
            {requirementsMet ? <ArrowRight className="h-4 w-4" /> : <LockKeyhole className="h-4 w-4" />}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
